/**
 * TikTok Pixel — client-side loader, gated on advertising consent.
 *
 * Gated on NEXT_PUBLIC_TIKTOK_PIXEL_ID AND advertising consent (hasAdsConsent():
 * explicit Accept OR the US/other opt-out ad default). If either is missing,
 * renders nothing. Polls hasAdsConsent() until consent resolves (or timeout),
 * then mounts the Pixel base code.
 *
 * Custom events should be fired via the exported tiktokEvent() helper;
 * pairs with server-side CAPI (src/lib/ads/tiktok-capi.ts) for
 * event_id deduplication.
 */
'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { useNonce } from '@/components/providers/NonceProvider';
import { hasAdsConsent } from '@/lib/consent/read';

declare global {
  interface Window {
    ttq?: { track: (event: string, params?: Record<string, unknown>) => void } & Record<string, unknown>;
    TiktokAnalyticsObject?: string;
  }
}

export function tiktokEvent(
  event: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;
  if (!window.ttq) return; // pixel not loaded yet (pre-consent or SSR)
  window.ttq.track(event, params);
}

export default function TikTokPixel() {
  const nonce = useNonce();
  const pixelId = process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID?.trim();
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    const check = () => { if (hasAdsConsent()) setConsented(true); };
    check();
    const idleId = window.requestIdleCallback?.(check, { timeout: 2000 })
      ?? setTimeout(check, 2000);
    const onStorage = () => check();
    window.addEventListener('storage', onStorage);
    return () => {
      if ('cancelIdleCallback' in window) window.cancelIdleCallback(idleId as number);
      else clearTimeout(idleId as unknown as ReturnType<typeof setTimeout>);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  if (!pixelId || !consented) return null;

  return (
    <Script
      id="tiktok-pixel-base"
      strategy="afterInteractive"
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html: `
          !function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=r;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};var script=d.createElement("script");script.type="text/javascript";script.async=true;script.src=r+"?sdkid="+e+"&lib="+t;var first=d.getElementsByTagName("script")[0];first.parentNode.insertBefore(script,first)};ttq.load('${pixelId}');ttq.page()}(window,document,'ttq');
        `,
      }}
    />
  );
}
